import { useAsyncData, useRouteLocationWithLocale } from '#imports'
import { type Ref, computed } from 'vue'

import type {
  AlgoliaLink,
  AlgoliaProduct,
} from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia.ts'
import type { SearchBarConfiguration } from '@backmarket/http-api/src/api-specs-search-reco/search/searchConfiguration.ts'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import algoliasearch from 'algoliasearch'

import { ROUTES as PRODUCT_LIST_ROUTES } from '~/scopes/product-list/route-names'
import {
  algoliaHitToLink,
  algoliaHitToProduct,
} from '~/scopes/search/algolia/algoliaFunctions'

import { MOCK_HISTORY } from './useRecentlyViewedPages.const'
import type { RecentlyViewedPage } from './useRecentlyViewedPages.types'

export function useRecentlyViewedPages(
  configuration: Ref<SearchBarConfiguration | undefined>,
) {
  const experiments = useExperiments()
  const resolveLocalizedRoute = useRouteLocationWithLocale()

  const isBrowsingHistoryEnabled =
    experiments['experiment.browsingHistoryPanel'] === 'enabled'
  const algoliaSearchOption = useAlgoliaSearchOptions('search', 'searchbar')
  const algoliaClient = computed(() => {
    if (!configuration.value) return null

    return algoliasearch('dummy_app_id', 'dummy_key', algoliaSearchOption)
  })

  return useAsyncData(
    'recentlyViewedPages',
    async () => {
      if (
        !isBrowsingHistoryEnabled ||
        !algoliaClient.value ||
        !configuration.value
      )
        return []

      const pdpIndexName =
        configuration.value.backboxWinners.indexes.active.name
      const plpIndexName = configuration.value.landingPages.indexes.active.name

      const requests = MOCK_HISTORY.flatMap((entry) => {
        if (entry.entryType === 'pdp') {
          return [
            {
              indexName: pdpIndexName,
              objectID: `${entry.uuid}_${entry.grade}_${entry.offerType}`,
            },
          ]
        }

        if (entry.entryType === 'plp') {
          return [
            {
              indexName: plpIndexName,
              objectID: entry.id,
            },
          ]
        }

        return []
      })

      if (requests.length === 0) return []

      const { results } = await algoliaClient.value.multipleGetObjects<
        AlgoliaProduct | AlgoliaLink
      >(requests)

      return results
    },
    {
      default: () => [] as RecentlyViewedPage[],
      transform: (results) => {
        const hitsByObjectId = new Map(
          results.map((hit) => [hit.objectID, hit]),
        )

        return MOCK_HISTORY.flatMap<RecentlyViewedPage>((entry) => {
          switch (entry.entryType) {
            case 'search-query': {
              return [
                {
                  id: entry.title,
                  highlightedTitle: entry.title,
                  title: entry.title,
                  url: resolveLocalizedRoute({
                    name: PRODUCT_LIST_ROUTES.SEARCH_RESULTS,
                    query: {
                      q: entry.title,
                    },
                  }),
                  entryType: 'search-query',
                  timestamp: entry.timestamp,
                },
              ]
            }
            case 'plp': {
              const hit = hitsByObjectId.get(entry.id) as
                | AlgoliaLink
                | undefined

              if (!hit) return []

              return [
                {
                  ...algoliaHitToLink(hit),
                  entryType: 'plp',
                  timestamp: entry.timestamp,
                },
              ]
            }
            case 'pdp': {
              const objectID = `${entry.uuid}_${entry.grade}_${entry.offerType}`
              const hit = hitsByObjectId.get(objectID) as
                | AlgoliaProduct
                | undefined

              if (!hit) return []

              return [
                {
                  ...algoliaHitToProduct(hit),
                  entryType: 'pdp',
                  timestamp: entry.timestamp,
                },
              ]
            }
            default:
              return []
          }
        })
      },
      server: false,
    },
  )
}
